import React, { useCallback, useMemo } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import metaConfig from 'src/assets/metaConfig.json';

interface RouteMetaProps {
    dynamicValues?: { [key: string]: string };
}

export const RouteMeta: React.FC<RouteMetaProps> = ({ dynamicValues = {} }) => {
    const location = useLocation();

    // Match current route based on dynamic URLs
    const currentRoute = useMemo(
        () =>
            Object.keys(metaConfig).find((key) => {
                const routePattern = metaConfig[key].url.replace(/:([^/]+)/g, '([^/]+)');
                const regex = new RegExp(`^${routePattern}$`);
                return regex.test(window.location.pathname);
            }),
        [location.pathname],
    );

    const meta = currentRoute ? metaConfig[currentRoute] : {};

    // Replace placeholders (e.g., {symbolId}) with dynamic values
    const processMetaValue = useCallback(
        (value: string | undefined): string => {
            if (!value) return '';
            return value.replace(/{([^}]+)}/g, (_, variable) =>
                dynamicValues[variable] ? dynamicValues[variable] : '',
            );
        },
        [dynamicValues],
    );

    const metaTitle = processMetaValue(meta.metaTitle);
    const metaDescription = processMetaValue(meta.metaDescription);
    const canonical = processMetaValue(meta.canonical);

    return (
        <HelmetProvider>
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaTitle && <meta name="title" content={metaTitle} />}
                {metaDescription && <meta name="description" content={metaDescription} />}
                {canonical && <link rel="canonical" href={canonical} />}
            </Helmet>
        </HelmetProvider>
    );
};
